/**
 * Module dependencies.
 */

import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Title } from 'app/components/atoms/title/title';
import { Spinner } from 'app/components/ui/spinner';
import { Button } from 'app/components/ui/button';
import { Input } from 'app/components/ui/input';
import { appRoutes } from 'app/routes';
import { useMe } from 'app/hooks/requests/use-me';
import { useToast } from 'app/components/providers/toast-provider/toast-provider';
import { Plus, X } from 'lucide-react';
import {
  useAddCryptoGestStablecoin,
  useCryptoGestStablecoins,
  useRemoveCryptoGestStablecoin
} from 'app/hooks/requests/crypto-gest/use-crypto-gest-admin';

/**
 * Export `AdministrationStablecoins` component.
 *
 * Admin-only. Manages the set of symbols treated as stablecoins, which drives
 * the "stablecoin exposure" figures shown across Crypto Gest.
 */

export function AdministrationStablecoins(): JSX.Element {
  const [translate] = useTranslation();
  const { success, error } = useToast();
  const { data: me } = useMe();
  const { data: symbols, isLoading } = useCryptoGestStablecoins();
  const add = useAddCryptoGestStablecoin();
  const remove = useRemoveCryptoGestStablecoin();
  const [value, setValue] = useState('');

  if (me && !me.isAdmin) {
    return <Navigate replace to={appRoutes.dashboard.home.base} />;
  }

  const onAdd = async () => {
    const symbol = value.trim().toUpperCase();

    if (!symbol) {
      return;
    }

    try {
      await add.mutateAsync(symbol);
      setValue('');
      success({ message: translate('cryptoGest.admin.stablecoins.added') });
    } catch {
      error({ message: translate('cryptoGest.admin.stablecoins.invalid') });
    }
  };

  const onRemove = async (symbol: string) => {
    try {
      await remove.mutateAsync(symbol);
      success({ message: translate('cryptoGest.admin.stablecoins.removed') });
    } catch {
      error({ message: translate('common.errors.network.default.title') });
    }
  };

  return (
    <div className={'space-y-5'}>
      <div>
        <Title>{translate('cryptoGest.admin.stablecoins.title')}</Title>

        <p className={'mt-1 text-sm text-muted-foreground'}>{translate('cryptoGest.admin.stablecoins.description')}</p>
      </div>

      <div className={'max-w-xl space-y-4 rounded-xl border border-border bg-card p-5 shadow-sm'}>
        <form
          className={'flex items-center gap-2'}
          onSubmit={event => {
            event.preventDefault();
            onAdd();
          }}
        >
          <Input
            onChange={event => setValue(event.target.value.toUpperCase())}
            placeholder={translate('cryptoGest.admin.stablecoins.placeholder')}
            value={value}
          />

          <Button disabled={add.isLoading || !value.trim()} type={'submit'}>
            <Plus className={'h-4 w-4'} />

            {translate('common.actions.add')}
          </Button>
        </form>

        {isLoading ? (
          <div className={'py-6'}>
            <Spinner />
          </div>
        ) : (symbols ?? []).length === 0 ? (
          <p className={'py-4 text-sm text-muted-foreground'}>{translate('cryptoGest.admin.stablecoins.empty')}</p>
        ) : (
          <div className={'flex flex-wrap gap-2'}>
            {(symbols ?? []).map(symbol => (
              <span
                className={
                  'inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 py-1 pl-3 pr-1.5 text-sm font-medium text-foreground'
                }
                key={symbol}
              >
                {symbol}

                <button
                  aria-label={translate('common.actions.remove')}
                  className={
                    'grid h-5 w-5 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive'
                  }
                  disabled={remove.isLoading}
                  onClick={() => onRemove(symbol)}
                  type={'button'}
                >
                  <X className={'h-3.5 w-3.5'} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
